import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Eye, RefreshCw, MoreHorizontal, ChevronDown, Loader2, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface RepostLoad {
  id: string;
  origin: string;
  destination: string;
  action: "fast" | "boost" | "ext";
  signal: string;
  badge: string;
  badgeTone: "amber" | "neutral" | "slate";
}

const BADGE_STYLES: Record<RepostLoad["badgeTone"], string> = {
  amber:   "bg-[hsl(38_75%_82%)] text-[hsl(30_65%_28%)]",
  neutral: "bg-muted text-muted-foreground",
  slate:   "bg-[hsl(215_18%_83%)] text-[hsl(215_28%_33%)]",
};

export function RepostPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [loads, setLoads] = useState<RepostLoad[]>([]);
  const [totalLoads, setTotalLoads] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }

    const fetch = async () => {
      try {
        const now = new Date();
        const { data } = await supabase
          .from('loads')
          .select('id, origin_city, origin_state, destination_city, destination_state, rate_amount, total_miles, updated_at, created_at, offers(id)')
          .eq('created_by', user.id)
          .eq('status', 'posted')
          .order('created_at', { ascending: false })
          .limit(20);

        if (!data) { setLoads([]); setTotalLoads(0); return; }

        setTotalLoads(data.length);
        const results: RepostLoad[] = [];
        for (const load of data) {
          const origin = `${load.origin_city || '?'}, ${load.origin_state || '?'}`;
          const dest = `${load.destination_city || '?'}, ${load.destination_state || '?'}`;
          const hoursSinceUpdate = (now.getTime() - new Date(load.updated_at || load.created_at || '').getTime()) / (1000 * 60 * 60);
          const offerCount = (load.offers || []).length;

          if (hoursSinceUpdate > 18) {
            results.push({ id: load.id, origin, destination: dest, action: "boost", signal: `${Math.floor(hoursSinceUpdate)}h since last update`, badge: "Stale Listing", badgeTone: "amber" });
          } else if (offerCount === 0 && hoursSinceUpdate > 8) {
            results.push({ id: load.id, origin, destination: dest, action: "ext", signal: `No carrier matches in ${Math.floor(hoursSinceUpdate)}h`, badge: "Low Visibility", badgeTone: "slate" });
          }
        }
        setLoads(results.slice(0, 7));
      } catch (err) {
        console.error('RepostPanel error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [user]);

  const handleBoost = async (loadId: string) => {
    const { error } = await supabase.from('loads').update({ updated_at: new Date().toISOString() }).eq('id', loadId);
    if (!error) {
      toast({ title: "Load boosted", description: "Visibility refreshed." });
      setLoads(prev => prev.filter(l => l.id !== loadId));
    }
  };

  return (
    <div className="flex flex-col rounded-lg border bg-card overflow-hidden h-full">
      <div className="flex items-center justify-between border-b bg-muted/20 px-4 py-2 flex-shrink-0">
        <h4 className="text-[12px] font-bold text-foreground">Top Loads To Repost</h4>
        <button className="text-muted-foreground hover:text-foreground transition-colors">
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center justify-between border-b bg-muted/10 px-4 py-1.5 flex-shrink-0">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">By Priority Score</span>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span className="text-[10px]">Analyzing…</span>
          </div>
        ) : totalLoads === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
            <Plus className="h-4 w-4" />
            <span className="text-[10px]">No loads posted yet</span>
            <button
              onClick={() => navigate('/post-load')}
              className="text-[10px] font-semibold text-primary hover:underline"
            >
              Post loads to get started
            </button>
          </div>
        ) : loads.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <span className="text-[10px]">All loads are up to date!</span>
          </div>
        ) : (
          loads.map((load, i) => (
            <div key={load.id} className={cn("flex items-center gap-3 px-4 py-2 hover:bg-muted/20 transition-colors", i < loads.length - 1 && "border-b")}>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <div className="h-4 w-4 rounded-full bg-muted border border-border flex items-center justify-center">
                  <Zap className="h-2 w-2 text-muted-foreground" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-[11px] font-semibold text-foreground whitespace-nowrap">
                    {load.origin} <span className="text-muted-foreground">→</span> {load.destination}
                  </p>
                  <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none whitespace-nowrap", BADGE_STYLES[load.badgeTone])}>
                    {load.badge}
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{load.signal}</p>
              </div>
              <div className="flex-shrink-0 flex items-center">
                <button
                  onClick={() => handleBoost(load.id)}
                  className="inline-flex items-center justify-center gap-1 rounded-md border border-border bg-muted px-3 h-[26px] text-[10px] font-semibold text-foreground hover:bg-muted/80 transition-colors whitespace-nowrap"
                >
                  <Eye className="h-2.5 w-2.5" /> Repost
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
